import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    bulk: {
        /** a part of the success toast message, which references groups if any */
        groups: (groupCount: number) => string | React.ReactNode;
        /** a part of the success toast message, which references members if any */
        members: (memberCount: number) => string | React.ReactNode;
        /** a part of the success toast message, which references guests if any */
        guests: (guestCount: number) => string | React.ReactNode;
        /** a part of the success toast message, which will be inserted between above parts if there is more than one part */
        and: string;
        /** a part of the success toast message that will come after the above parts are listed and will mention the name of the resource to which they are added */
        addedTo: (resourceName: string) => string | React.ReactNode;
        /** Error toast message when bulk member addition fails */
        error: (count: number, resourceName: string) => string | React.ReactNode;
    };
    single: {
        /** Success toast message when adding a single member to resource */
        success: (userType: "Guest" | "Member" | "Group", resourceName: string) => string | React.ReactNode;
        /** Error toast message when single member addition fails */
        error: (resourceName: string) => string | React.ReactNode;
    };
    singleNew: {
        /** Success toast message when sending invitation to new user */
        success: (memberEmail: string) => string | React.ReactNode;
        /** Error toast message when invitation fails */
        error: (resourceName: string) => string | React.ReactNode;
    };
    missingResourceIdError: string;
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            bulk: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** a part of the success toast message, which references groups if any */
                        groups: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (groupCount: number) => __intl__.intl().formatMessage({ id: "useCreateMemberships.bulk.groups?c=l3fljszf3yo7", defaultMessage: "{groupCount, plural, =0 {} one {1 Group } other {{groupCount} Groups }}" }, { groupCount: groupCount }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** a part of the success toast message, which references members if any */
                        members: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (memberCount: number) => __intl__.intl().formatMessage({ id: "useCreateMemberships.bulk.members?c=3f3jr1j8aywam", defaultMessage: "{memberCount, plural, =0 {} one {1 Member } other {{memberCount} Members }}" }, { memberCount: memberCount }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** a part of the success toast message, which references guests if any */
                        guests: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (guestCount: number) => __intl__.intl().formatMessage({ id: "useCreateMemberships.bulk.guests?c=195cqtc6oxjel", defaultMessage: "{guestCount, plural, =0 {} one {1 Guest } other {{guestCount} Guests }}" }, { guestCount: guestCount }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** a part of the success toast message, which will be inserted between above parts if there is more than one part */
                        and: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useCreateMemberships.bulk.and?c=17zszpbk8biek", defaultMessage: "and" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** a part of the success toast message that will come after the above parts are listed and will mention the name of the resource to which they are added */
                        addedTo: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (resourceName: string) => __intl__.intl().formatMessage({ id: "useCreateMemberships.bulk.addedTo?c=1ppye3c0y6xlt", defaultMessage: " added to {resourceName}" }, { resourceName: resourceName }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Error toast message when bulk member addition fails */
                        error: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (count: number, resourceName: string) => __intl__.intl().formatMessage({ id: "useCreateMemberships.bulk.error?c=8cbfvuk96but", defaultMessage: "Unable to add {count} members to {resourceName}" }, { count: count, resourceName: resourceName }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            single: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Success toast message when adding a single member to resource */
                        success: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (userType: "Guest" | "Member" | "Group", resourceName: string) => __intl__.intl().formatMessage({ id: "useCreateMemberships.single.success?c=3t8e9xzxlqcw9", defaultMessage: "{userType} added to {resourceName}" }, { userType: userType, resourceName: resourceName }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Error toast message when single member addition fails */
                        error: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (resourceName: string) => __intl__.intl().formatMessage({ id: "useCreateMemberships.single.error?c=iy82xh85e2wf", defaultMessage: "Unable to add membership to {resourceName}" }, { resourceName: resourceName }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            singleNew: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Success toast message when sending invitation to new user */
                        success: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (memberEmail: string) => __intl__.intl().formatMessage({ id: "useCreateMemberships.singleNew.success?c=1b55m4y7o2raw", defaultMessage: "Invitation sent to {memberEmail}" }, { memberEmail: memberEmail }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Error toast message when invitation fails */
                        error: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (resourceName: string) => __intl__.intl().formatMessage({ id: "useCreateMemberships.singleNew.error?c=1d2vwns6tgc2l", defaultMessage: "Unable to invite to {resourceName}" }, { resourceName: resourceName }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            /** Error message when resource ID is missing */
            missingResourceIdError: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useCreateMemberships.missingResourceIdError?c=1h4a4b3avcgjn", defaultMessage: "Unable to add membership" }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
